// Orchestrator: nimmt eine PDF-Datei und liefert pre-parsed Rechnungs-
// Felder. Aktuell zwei Stufen:
//   1) QR-Scan (BarcodeDetector / jsQR, multi-pass)
//   2) Text-Layer-Analyse fuer Urkundennummer, Betrag, IBAN — als
//      Fallback wenn der QR fehlt oder seinen Verwendungszweck nicht
//      mit der Urkundennummer befuellt. Kein OCR.

import { detectQrInPages } from './qrDetect';
import { parseQrPayload, type EpcParseResult } from './epcParser';
import { extractPdfText, renderPdfPages } from './pdfToImages';
import {
  extractInvoiceFieldsFromText,
  type TextScanResult,
} from './textParser';

export interface InvoiceScanResult {
  /** QR-Rohinhalt (kompatibel zu `EPCString` im Backend). */
  qrPayload: string | null;
  /** Geparste EPC-Felder, sofern QR gefunden wurde. */
  parsed: EpcParseResult | null;
  /** Aus dem Text-Layer extrahierte Felder (immer ausgefuellt, sofern
   *  ein Text-Layer existiert). Werden ans Backend nur weitergereicht,
   *  wenn das QR-Resultat sie nicht abdeckt. */
  textScan: TextScanResult | null;
  /** Finale Felder zum Senden ans Backend — bereits aus QR + Text-Layer
   *  zusammengefuehrt. */
  combined: {
    EPCString: string | null;
    Total: number | null;
    InvoiceNumber: string;
    IBAN: string;
    BIC: string;
    Recipient: string;
  };
}

function looksLikeRecordNumber(s: string): boolean {
  // Akzeptiert "K 333/0/1 - 2026" sowie "K 12/2024".
  return /\b[A-Z]?\s*\d{1,6}\s*\/\s*\d{1,6}(?:\s*\/\s*\d{1,6})?\s*[-–]?\s*\d{2,4}\b/i.test(
    s
  );
}

export async function scanInvoicePdf(
  source: ArrayBuffer | Uint8Array
): Promise<InvoiceScanResult> {
  // PDF-Text fuer den Fallback brauchen wir spaeter — ziehen wir schon
  // einmal raus, weil das parallel zum Rendering quasi nichts kostet.
  // pdf.js liest das Dokument ohnehin zweimal aus dem ArrayBuffer.
  const textPromise = extractPdfText(source).catch(() => '');

  const pages = await renderPdfPages(source);
  const qr = await detectQrInPages(pages);
  const parsed = qr ? parseQrPayload(qr) : null;

  const text = await textPromise;
  const textScan = text ? extractInvoiceFieldsFromText(text) : null;

  // Zusammenfuehren: QR hat Vorrang, Text-Layer fuellt nur fehlende Felder
  // oder uebersteuert die InvoiceNumber, wenn der QR-Purpose nicht wie
  // eine Urkundennummer aussieht (z. B. weil der Versender dort einen
  // generischen Verwendungszweck wie "Rechnung Holland" eingetragen hat).
  const qrPurpose = parsed?.purpose ?? '';
  const useTextForNumber =
    !looksLikeRecordNumber(qrPurpose) && (textScan?.invoiceNumber ?? '') !== '';
  const combined = {
    EPCString: qr,
    Total: parsed?.total ?? textScan?.total ?? null,
    InvoiceNumber: useTextForNumber ? textScan!.invoiceNumber : qrPurpose,
    IBAN: parsed?.iban || textScan?.iban || '',
    BIC: parsed?.bic ?? '',
    Recipient: parsed?.recipient ?? '',
  };

  return { qrPayload: qr, parsed, textScan, combined };
}

export { parseQrPayload };
export type { EpcParseResult };
